import { useGetCostEstimate } from '@/api/services/costEstimateService';
import { useOrg } from '@/organization/useOrg';
import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { CheckCircle } from '@mui/icons-material';
import { Box, Divider, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BuildingAlternative } from '../stores/SubprojectStore.ts';
import { rollUpBuildingAlternatives } from './buildingAlternativeRollup.ts';
import { DecarbHubBuildingDetails } from './DecarbHubBuildingDetails.tsx';
import { DecarbHubCostSection } from './DecarbHubCostSection.tsx';
import { DecarbHubEnergySection } from './DecarbHubEnergySection.tsx';
import { DecarbHubTile } from './DecarbHubTile.ts';
import { DecarbHubTileHeader } from './DecarbHubTileHeader.tsx';
import { computeOperatingWedges } from './operatingWedges.ts';
import { EnergyKind } from './types';

interface DecarbOverviewTileProps {
  usage: string;
  getColor: (index: number) => string;
  alternatives: BuildingAlternative[];
  compares?: BuildingAlternative[];
  pmPreferred?: boolean;
  boApproved?: boolean;
  accent?: boolean;
  designLifeDisplay?: string | null;
  newOrRenewalDisplay?: string | null;
  constructionCostSum?: number;
  designCostSum?: number;
}
export const DecarbOverviewTile: React.FC<DecarbOverviewTileProps> = ({
  usage,
  getColor,
  alternatives,
  compares,
  pmPreferred,
  boApproved,
  accent,
  designLifeDisplay,
  newOrRenewalDisplay,
  constructionCostSum,
  designCostSum,
}) => {
  const { convertArea } = useEnumHelpers();
  const { selectedOrg } = useOrg();
  const { projectId } = useParams();
  const { data: costEstimate } = useGetCostEstimate(selectedOrg?.id, projectId);

  const alternative = useMemo(() => {
    if (alternatives.length === 0) return undefined;
    return rollUpBuildingAlternatives(alternatives, convertArea);
  }, [alternatives, convertArea]);

  const compare = useMemo(() => {
    if (!compares || compares.length === 0) return undefined;
    return rollUpBuildingAlternatives(compares, convertArea);
  }, [compares, convertArea]);

  const annualEmissions = useMemo(() => {
    if (!alternatives || alternatives.length === 0) return 0;
    return alternatives.reduce((sum, alt) => sum + (alt?.tonnesCo2 ?? 0), 0);
  }, [alternatives]);

  const energyOnsiteSupply =
    alternative?.energyOnsiteSupply ?? ({} as Record<EnergyKind, number>);

  // Conventional equipment maintenance drives the minor-maintenance delta. On the
  // Conventional tile `compare` is undefined (this tile IS Conventional); on the
  // LC/NZ tiles `compare` is the rolled-up Conventional baseline.
  const conventionalEquipMaintenance =
    (compare ?? alternative)?.costs?.maintenanceCost ?? 0;

  const extraAnnualWedges = useMemo(
    () => computeOperatingWedges(costEstimate, conventionalEquipMaintenance),
    [costEstimate, conventionalEquipMaintenance],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'visible',
        height: '100%',
        border:
          pmPreferred || boApproved
            ? '2px solid black'
            : '2px solid transparent',
        borderRadius: 1,
      }}
    >
      <DecarbHubTile accent={accent}>
        <DecarbHubTileHeader usage={usage} annualEmissions={annualEmissions} />
        <DecarbHubBuildingDetails
          buildingParams={alternative?.buildingParams}
          designLifeDisplay={designLifeDisplay}
          newOrRenewalDisplay={newOrRenewalDisplay}
        />
        <Divider />
        {alternative && (
          <DecarbHubCostSection
            alternative={alternative}
            alternativeCompare={compare}
            getColor={getColor}
            designCost={designCostSum || 0}
            constructionCost={constructionCostSum || 0}
            annualCostLabel='Annual Costs'
            extraAnnualWedges={extraAnnualWedges}
          />
        )}
        <Divider />
        {alternative && (
          <DecarbHubEnergySection
            alternative={alternative}
            alternativeCompare={compare}
            getColor={getColor}
            energyDemand={alternative.energyDemand}
            energyOnsiteSupply={energyOnsiteSupply}
          />
        )}
      </DecarbHubTile>
      {pmPreferred && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -8,
            left: -8,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: 'info.main',
              color: 'primary.main',
              borderRadius: 2,
              border: '2px solid black',
              px: 3,
              py: 1.5,
            }}
          >
            <CheckCircle sx={{ fontSize: 14 }} />
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              PM Preferred
            </Typography>
          </Box>
        </Box>
      )}
      {boApproved && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -8,
            right: -8,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: 'success.main',
              color: 'primary.main',
              borderRadius: 2,
              border: '2px solid black',
              px: 3,
              py: 1.5,
            }}
          >
            <CheckCircle sx={{ fontSize: 14 }} />
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              BO Approved
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
